import { AxiosError } from 'axios';
import { logger } from './shared/logger';

export interface ApiError {
  error: {
    code: string;
    message: string;
    details?: Record<string, any>;
  };
}

interface ApiErrorResponse {
  error: {
    code: string;
    message: string;
    details?: Record<string, any>;
  };
}

function safeStringify(value: any): string {
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

// Recursively extract the most relevant error message
function extractMessage(error: any): string {
  if (!error) return 'An unexpected error occurred';
  if (typeof error === 'string') {
    try {
      // Try to parse stringified JSON
      const parsed = JSON.parse(error);
      return extractMessage(parsed);
    } catch {
      return error;
    }
  }
  if (typeof error === 'object') {
    if (error.message) return extractMessage(error.message);
    if (error.detail) return extractMessage(error.detail);
    if (error.error) return extractMessage(error.error);
    if (error.code && typeof error.code === 'string' && error.code !== 'API_ERROR' && error.code !== 'UNEXPECTED_ERROR' && error.code !== 'NETWORK_ERROR') return error.code;
    // Try to find a message in nested details
    if (error.details) return extractMessage(error.details);
  }
  return 'An unexpected error occurred';
}

export class ErrorHandler {
  static handle(error: unknown): ApiError {
    if (this.isAxiosError(error)) {
      const responseData = error.response?.data as any;
      // If backend returns { detail: "...", ... }
      if (responseData?.detail) {
        return {
          error: {
            code: responseData.code || 'API_ERROR',
            message: extractMessage(responseData.detail),
            details: responseData,
          }
        };
      }
      // If backend returns { error: ... }
      if (responseData?.error) {
        // If error is an object, try to extract a string message, else stringify the whole object
        if (typeof responseData.error === 'object') {
          let message = '';
          if (typeof responseData.error.message === 'string') {
            message = extractMessage(responseData.error.message);
          } else if (typeof responseData.error.detail === 'string') {
            message = extractMessage(responseData.error.detail);
          } else {
            message = extractMessage(responseData.error);
          }
          return {
            error: {
              code: responseData.error.code || 'API_ERROR',
              message,
              details: responseData.error.details || responseData.error,
            }
          };
        } else {
          return {
            error: {
              code: 'API_ERROR',
              message: extractMessage(responseData.error),
              details: responseData,
            }
          };
        }
      }
      // Handle network errors
      if (error.request) {
        logger.error('Network Error:', { error: safeStringify(error.message) });
        return {
          error: {
            code: 'NETWORK_ERROR',
            message: 'Network error. Please check your connection.',
            details: { originalError: error.message }
          }
        };
      }
    }

    // Fallback for unexpected errors
    let originalError: any;
    if (error instanceof Error) {
      // Try to parse error.message as JSON if possible
      try {
        originalError = JSON.parse(error.message);
      } catch {
        originalError = error.message;
      }
    } else {
      originalError = error;
    }

    logger.error('Unexpected Error:', { error });
    return {
      error: {
        code: 'UNEXPECTED_ERROR',
        message: extractMessage(error),
        details: { originalError }
      }
    };
  }

  private static isAxiosError(error: unknown): error is AxiosError {
    return (error as AxiosError)?.isAxiosError === true;
  }

  static getErrorMessage(error: unknown): string {
    const apiError = this.handle(error);
    return apiError.error.message;
  }

  static getErrorCode(error: unknown): string {
    const apiError = this.handle(error);
    return apiError.error.code;
  }

  static getErrorDetails(error: unknown): Record<string, any> | undefined {
    const apiError = this.handle(error);
    return apiError.error.details;
  }
} 